import { cn } from "@simpro/ui";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import { amenityLabel } from "../lib/amenities";

/** What each catalogue facility promises (Phase 4 design D2; the catalogue is the server's). */
const FACILITY: Record<string, string> = {
  AirConditioning: "Cool and comfortable in every season.",
  WiFi: "Stay connected while you wait.",
  Parking: "Space to park close to the door.",
  WheelchairAccess: "Step-free from the street to the chair.",
  KidsFriendly: "Little ones are welcome too.",
  BeverageService: "Something to drink while you relax.",
  PrivateRoom: "A private room when you would like one.",
};

/** One thing the salon wrote about itself: a title and a line (its icon, from the list, is not drawn on this design). */
export interface HighlightItem {
  icon: string;
  title: string;
  text: string | null;
}

export interface SalonHighlightsProps {
  name: string;
  facilities: string[];
  paymentOptions: string[];
  /** The salon's own heading (FR-SA-191); "At {name}" when it wrote none. */
  heading?: string | null;
  /** The salon's about text (FR-SA-191), under the heading; none, no lead. */
  intro?: string | null;
  /** The salon's own items, which take the place of the facilities when it wrote any (FR-SA-191). */
  items?: readonly HighlightItem[];
}

/** The board's three circle colours, in turn: pale sage, blush and the sage-pale well. */
const DISCS = ["bg-sage-tint", "bg-tint", "bg-muted-bg"] as const;

/** i, ii, iii… for the circles, as the board numbers its three. */
const ROMAN = ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii"] as const;

/**
 * "What we offer" (design note salon-home-landing.md section 5, item 4; FR-SA-190): what the salon
 * wrote about itself, else each facility it has ticked with a line about it, then how to pay at the
 * counter. Nothing when the salon has chosen neither. Payment is information only: nothing is paid in
 * the app (TDD-SA D6).
 *
 * Drawn as the Organic Wellness philosophy band (design note salon-booking-organic-wellness.md
 * section 6 item 4): a sand band the width of the page, the salon's heading and its about text, then
 * the items in a row, each under a numbered circle in pale sage, blush or sage-pale, its title in the
 * display face, rising into view in turn.
 */
export function SalonHighlights({ name, facilities, paymentOptions, heading, intro, items = [] }: SalonHighlightsProps) {
  const own = items.length > 0;
  if (!own && facilities.length === 0 && paymentOptions.length === 0) return null;

  const rows: { key: string; title: string; text: string | null }[] = own
    ? items.map((item) => ({ key: item.title, title: item.title, text: item.text }))
    : facilities.map((value) => ({ key: value, title: amenityLabel(value), text: FACILITY[value] ?? null }));

  return (
    <section aria-labelledby="highlights-heading" className="bg-surface">
      <div className={cn(page, "flex flex-col gap-12 py-16 md:py-24 lg:py-28")}>
        <SectionHeading id="highlights-heading" title={heading ?? `At ${name}`} lead={intro ?? null} />

        {rows.length > 0 ? (
          <ul
            aria-label={own ? "Why choose us" : "Facilities"}
            className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,16rem),1fr))] gap-10"
          >
            {rows.map(({ key, title, text }, index) => (
              <Reveal as="li" key={key} index={index} className="flex flex-col gap-3.5">
                <span
                  aria-hidden="true"
                  className={cn(
                    "headline grid size-16 place-items-center rounded-full text-[1.75rem] text-heading",
                    DISCS[index % DISCS.length],
                  )}
                >
                  {ROMAN[index] ?? index + 1}
                </span>
                <h3 className="headline text-[2rem] leading-tight text-heading">{title}</h3>
                {text ? <p className="text-base leading-[1.7] text-content">{text}</p> : null}
              </Reveal>
            ))}
          </ul>
        ) : null}

        {paymentOptions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-3 text-sm text-subtle">
            <span>Pay at the salon by</span>
            <ul aria-label="Payment options" className="flex flex-wrap gap-2">
              {paymentOptions.map((value) => (
                <li
                  key={value}
                  className="rounded-full bg-base px-3.5 py-1.5 text-xs font-semibold text-heading ring-1 ring-edge-subtle ring-inset"
                >
                  {amenityLabel(value)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}
